// Settings -> Operating parameters: the figures the registers run to (deadlines, review cycles,
// retention), where each one comes from, and the figures the organisation's adopted policies
// propose. Rules on the server in backend/src/lib/operatingParameters.ts; contract in
// docs/OPERATING-PARAMETERS.md. Applying, dismissing and editing need org:admin (the server decides;
// `canEdit` only hides the controls).

import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, RefreshCw, SlidersHorizontal, AlertTriangle } from "lucide-react";
import { api, apiErrorMessage } from "../api/client";
import { SheetCard, DimensionHeading, StatusTag } from "../components/Drawing";
import { PortalLink } from "../components/PortalLink";
import {
  formatParameterValue, loadOperatingParameters,
  type OperatingParameterView, type OperatingParametersResponse,
} from "../components/PolicySaysChip";
import { theme, btnPrimary, btnSecondary, inputStyle, emptyFrame, countTag, pageHeading, pageSubtitle, type as text } from "../theme";
import { fmtDate } from "../lib/format";

const SOURCE_LABEL: Record<OperatingParameterView["source"], string> = {
  default: "Platform default",
  manual: "Set by hand",
  policy: "Your policy",
};

export function OperatingParametersPage() {
  const [data, setData] = useState<OperatingParametersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await loadOperatingParameters(true));
      setError(null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the operating parameters"));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const act = async (id: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(id);
    setNotice(null);
    try {
      await fn();
      setNotice(done);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "That did not work"));
    } finally {
      setBusy(null);
    }
  };

  const groups = useMemo(() => {
    const out = new Map<string, { label: string; rows: OperatingParameterView[] }>();
    for (const p of data?.parameters ?? []) {
      const g = out.get(p.group) ?? { label: p.groupLabel, rows: [] };
      g.rows.push(p);
      out.set(p.group, g);
    }
    return [...out.values()];
  }, [data]);

  const byKey = useMemo(() => new Map((data?.parameters ?? []).map((p) => [p.key, p])), [data]);
  const pending = (data?.proposals ?? []).filter((p) => p.status === "pending");
  const refused = (data?.proposals ?? []).filter((p) => p.status === "refused");

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
            <SlidersHorizontal size={20} /> Operating parameters
          </h1>
          <p style={{ ...pageSubtitle, maxWidth: 780 }}>
            The figures your registers run to: how soon a vulnerability must be fixed, when a supplier questionnaire is chased,
            how often access is reviewed. Your adopted policies are read for the figures they commit you to, and each one is offered
            here for you to apply. A figure looser than a law or scheme you work to is never applied.
          </p>
        </div>
        {data?.canEdit && data.aiAvailable && (
          <button
            type="button" style={{ ...btnSecondary, display: "flex", alignItems: "center", gap: 6 }} disabled={busy === "extract"}
            title="Read the latest adopted copy of each policy that has not been read yet"
            onClick={() => act("extract", async () => {
              const r = await api.post<{ queued: number; policies: number }>("/operating-parameters/extract");
              return r;
            }, "Your adopted policies are being read. New figures appear here when it finishes.")}
          >
            <RefreshCw size={13} /> Read my adopted policies
          </button>
        )}
      </div>

      {error && <div style={{ ...text.body, color: theme.color.dangerText, marginBottom: 12 }}>{error}</div>}
      {notice && <div style={{ ...text.body, color: theme.color.successText, marginBottom: 12 }}>{notice}</div>}
      {!data && !error && <div style={text.hint}>Loading…</div>}

      {data && data.drift.length > 0 && (
        <SheetCard label="Where your registers and policies differ" reference={<span style={countTag}>{data.drift.length}</span>} style={{ marginBottom: 16 }}>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 10 }}>
            {data.drift.map((d) => (
              <li key={d.id} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <AlertTriangle size={14} color={d.severity === "high" ? theme.color.danger : theme.color.warning} style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={text.itemTitle}>{d.title}</div>
                  <div style={text.hint}>{d.description}</div>
                </div>
              </li>
            ))}
          </ul>
        </SheetCard>
      )}

      {data && (pending.length > 0 || refused.length > 0) && (
        <SheetCard label="Figures from your adopted policies" reference={<span style={countTag}>{pending.length + refused.length}</span>} featured style={{ marginBottom: 16 }}>
          <div style={{ display: "grid", gap: 14 }}>
            {[...pending, ...refused].map((p) => {
              const param = byKey.get(p.parameterKey);
              if (!param) return null;
              const current = formatParameterValue(param.value, param.unit);
              const proposed = formatParameterValue(p.value, param.unit);
              return (
                <div key={p.id} style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
                    <div style={text.itemTitle}>
                      {param.label}: {proposed} <span style={{ ...text.hint, fontWeight: 400 }}>(now {current}, {SOURCE_LABEL[param.source].toLowerCase()})</span>
                    </div>
                    {p.status === "refused" && <StatusTag tone="gap">Weaker than the law or scheme</StatusTag>}
                  </div>
                  <blockquote style={{ margin: "6px 0", padding: "4px 10px", borderLeft: `3px solid ${theme.color.ref}`, ...text.body }}>
                    {p.quote}
                  </blockquote>
                  <div style={text.meta}>{p.policyTitle}, version {p.documentVersion} · read {fmtDate(p.createdAt)}</div>
                  {p.status === "refused" && p.limit && (
                    <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 4 }}>
                      {p.limit.basis} ({formatParameterValue(p.limit.value, param.unit)}). Correct the policy and adopt the corrected copy.
                    </div>
                  )}
                  {p.status === "pending" && data.canEdit && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button type="button" style={btnPrimary} disabled={busy === p.id}
                        onClick={() => act(p.id, () => api.post(`/operating-parameters/proposals/${p.id}/apply`), `${param.label} now runs at ${proposed}.`)}>
                        Apply {proposed}
                      </button>
                      <button type="button" style={btnSecondary} disabled={busy === p.id}
                        onClick={() => act(p.id, () => api.post(`/operating-parameters/proposals/${p.id}/dismiss`, {}), `Kept ${param.label.toLowerCase()} at ${current}.`)}>
                        Keep {current}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SheetCard>
      )}

      {groups.map((g) => (
        <section key={g.label} style={{ marginBottom: 18 }}>
          <DimensionHeading count={g.rows.length}>{g.label}</DimensionHeading>
          <SheetCard padding={0}>
            <table style={{ width: "100%", borderCollapse: "collapse", ...text.body }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.color.line}`, textAlign: "left" }}>
                  <th style={th}>Parameter</th>
                  <th style={th}>Value</th>
                  <th style={th}>Source</th>
                  <th style={th}>Used by</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((p) => <ParameterRow key={p.key} p={p} canEdit={!!data?.canEdit} busy={busy === p.key}
                  onSave={(value) => act(p.key, () => api.patch("/operating-parameters", { key: p.key, value }), value === null ? `${p.label} is back on the platform default.` : `${p.label} saved.`)} />)}
              </tbody>
            </table>
          </SheetCard>
        </section>
      ))}

      {data && (
        <section style={{ marginBottom: 18 }}>
          <DimensionHeading count={data.extractions.length}>Policies read</DimensionHeading>
          {data.extractions.length === 0 ? (
            <div style={emptyFrame}>
              No adopted policy has been read yet. A policy is read when you adopt it (upload your reviewed copy from{" "}
              <PortalLink to="/templates" style={{ color: theme.color.ref }}>Policy templates</PortalLink>, or save it in your connected storage).
            </div>
          ) : (
            <SheetCard padding={0}>
              <table style={{ width: "100%", borderCollapse: "collapse", ...text.body }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.color.line}`, textAlign: "left" }}>
                    <th style={th}>Policy</th><th style={th}>Copy</th><th style={th}>Result</th><th style={th}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.extractions.map((e) => (
                    <tr key={e.id} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                      <td style={td}>{e.policyTitle}</td>
                      <td style={td}>{e.fileName} (v{e.documentVersion})</td>
                      <td style={td}>
                        {e.status === "done" ? `${e.found} figure${e.found === 1 ? "" : "s"} found`
                          : e.status === "queued" || e.status === "running" ? "Being read"
                          : <span style={{ color: e.status === "error" ? theme.color.dangerText : theme.color.subtle }}>{e.error ?? e.status}</span>}
                      </td>
                      <td style={td}>{fmtDate(e.completedAt ?? e.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SheetCard>
          )}
        </section>
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "var(--c-muted)", textTransform: "uppercase", letterSpacing: "0.04em" };
const td: React.CSSProperties = { padding: "8px 12px", verticalAlign: "top" };

function ParameterRow({ p, canEdit, busy, onSave }: { p: OperatingParameterView; canEdit: boolean; busy: boolean; onSave: (value: number | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(p.configured));
  useEffect(() => { setDraft(String(p.configured)); }, [p.configured]);
  const value = Number(draft);
  const valid = Number.isInteger(value) && value >= p.min && value <= p.max && (!p.options || p.options.includes(value));

  return (
    <tr style={{ borderBottom: `1px solid ${theme.color.border}` }}>
      <td style={td}>
        <div style={{ fontWeight: 600, color: theme.color.heading }}>{p.label}</div>
        {p.limit && (
          <div style={text.meta}>
            Limit: {p.tighter === "lower" ? "at most" : "at least"} {formatParameterValue(p.limit.value, p.unit)} ({p.limit.basis})
          </div>
        )}
      </td>
      <td style={{ ...td, whiteSpace: "nowrap" }}>
        {editing ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {p.options ? (
              <select style={{ ...inputStyle, width: "auto" }} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label={p.label}>
                {p.options.map((o) => <option key={o} value={o}>{formatParameterValue(o, p.unit)}</option>)}
              </select>
            ) : (
              <input type="number" min={p.min} max={p.max} step={1} style={{ ...inputStyle, width: 90 }} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label={p.label} />
            )}
            <span style={text.hint}>{p.unit === "score" ? "on the 1-25 risk scale" : p.unit === "percent" ? "%" : p.unit === "count" ? "" : p.unit}</span>
            <button type="button" style={btnPrimary} disabled={!valid || busy} onClick={() => { onSave(value); setEditing(false); }}>Save</button>
            <button type="button" style={btnSecondary} onClick={() => { setDraft(String(p.configured)); setEditing(false); }}>Cancel</button>
          </div>
        ) : (
          <div>
            <span style={{ fontWeight: 600 }}>{formatParameterValue(p.value, p.unit)}</span>
            {p.capped && <div style={{ ...text.meta, color: theme.color.warningText }}>{p.source === "default" ? "The platform default" : "Set"} is {formatParameterValue(p.configured, p.unit)}; shortened to the limit that applies to you</div>}
            {canEdit && (
              <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                <button type="button" onClick={() => setEditing(true)} style={linkBtn}>Change</button>
                {p.source !== "default" && <button type="button" disabled={busy} onClick={() => onSave(null)} style={linkBtn}>Use the default ({formatParameterValue(p.defaultValue, p.unit)})</button>}
              </div>
            )}
          </div>
        )}
      </td>
      <td style={td}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {p.source === "policy" ? <FileText size={12} color={theme.color.ref} /> : <SlidersHorizontal size={12} color={theme.color.subtle} />}
          {SOURCE_LABEL[p.source]}
        </div>
        {p.source === "policy" && p.stored?.quote && (
          <div style={{ ...text.hint, marginTop: 4 }}>
            "{p.stored.quote}" <span style={text.meta}>({p.stored.policyTitle ?? "adopted policy"}{p.stored.documentVersion ? `, v${p.stored.documentVersion}` : ""})</span>
          </div>
        )}
        {p.source === "default" && <div style={text.meta}>{p.decisions.length ? `No policy has set this yet. These policies can: ${[...new Set(p.decisions.map((d) => d.templateTitle ?? d.templateId))].join(", ")}` : "No policy states this; set it by hand"}</div>}
      </td>
      <td style={td}>
        {p.appliedBy.length ? p.appliedBy.map((a) => <div key={a} style={text.hint}>{a}</div>) : <div style={text.meta}>Record checks and generated documents</div>}
      </td>
    </tr>
  );
}

const linkBtn: React.CSSProperties = { background: "none", border: "none", padding: 0, color: "var(--c-ref)", cursor: "pointer", fontSize: 12 };
